import React from 'react';
import { Ban, RefreshCw, Zap, Monitor } from 'lucide-react';

const translations = {
  de: {
    title: 'Warum',
    brand: 'weltwelt365',
    question: '?',
    features: [
      {
        icon: Ban,
        title: 'Keine Werbung',
        description: 'Unsere Premium-Spiele werden durch keinerlei Werbung unterbrochen. Auf diese Weise können Sie unsere Spiele in vollen Zügen genießen.',
      },
      {
        icon: RefreshCw,
        title: 'Häufige Aktualisierungen',
        description: 'Wir mögen keine langweiligen Momente, deshalb halten wir unser Spieleportal häufig aktualisiert, so dass Sie die besten und neuesten Spiele jederzeit und überall genießen können.',
      },
      {
        icon: Zap,
        title: 'Sofortiges Spiel',
        description: 'Unsere Premium-Spiele laufen auf HTML5, was bedeutet, dass Sie sie nicht installieren müssen und sofort spielen können.',
      },
      {
        icon: Monitor,
        title: 'Kreuzgerät',
        description: 'Als Mitglied können Sie sich auf unserem Spielportal mit jedem Gerät einloggen. Egal, ob es sich um ein Mobiltelefon, ein Tablet oder einen Laptop handelt. Holen Sie mit weltwelt365 das Beste aus Ihren Geräten heraus.',
      },
    ],
  },
  en: {
    title: 'Why',
    brand: 'weltwelt365',
    question: '?',
    features: [
      {
        icon: Ban,
        title: 'No Ads',
        description: 'Our premium games are not interrupted by any advertising. This way you can fully enjoy our games.',
      },
      {
        icon: RefreshCw,
        title: 'Frequent Updates',
        description: "We don't like boring moments, so we keep our gaming portal frequently updated so you can enjoy the best and latest games anytime, anywhere.",
      },
      {
        icon: Zap,
        title: 'Instant Play',
        description: 'Our premium games run on HTML5, which means you don\'t need to install them and can play instantly.',
      },
      {
        icon: Monitor,
        title: 'Cross-Device',
        description: 'As a member, you can log into our gaming portal with any device. Whether it\'s a mobile phone, tablet or laptop. Get the most out of your devices with weltwelt365.',
      },
    ],
  }
};

function Features({ language }) {
  const t = translations[language];

  return (
    <section id="features" className="relative py-24 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent"></div>
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-accent/50 to-transparent"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
            {t.title} <span className="gradient-text">{t.brand}</span>{t.question}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary via-secondary to-accent mx-auto rounded-full"></div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {t.features.map((feature, index) => {
            const IconComponent = feature.icon;
            const colors = [
              { bg: 'from-rose-500/20 to-orange-500/20', border: 'border-rose-500/30', icon: 'text-rose-400', glow: 'group-hover:shadow-rose-500/30' },
              { bg: 'from-cyan-500/20 to-blue-500/20', border: 'border-cyan-500/30', icon: 'text-cyan-400', glow: 'group-hover:shadow-cyan-500/30' },
              { bg: 'from-amber-500/20 to-yellow-500/20', border: 'border-amber-500/30', icon: 'text-amber-400', glow: 'group-hover:shadow-amber-500/30' },
              { bg: 'from-violet-500/20 to-purple-500/20', border: 'border-violet-500/30', icon: 'text-violet-400', glow: 'group-hover:shadow-violet-500/30' },
            ];
            const color = colors[index];
            
            return (
              <div
                key={index}
                className={`group relative p-8 bg-gradient-to-br ${color.bg} backdrop-blur-sm rounded-2xl border ${color.border} card-hover cursor-default ${color.glow}`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Icon */}
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-surface/80 ${color.icon} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <IconComponent className="w-8 h-8" />
                </div>
                
                {/* Content */}
                <h3 className="text-xl sm:text-2xl font-heading font-bold text-white mb-4">
                  {feature.title}
                </h3>
                <p className="text-slate-400 font-body text-lg leading-relaxed">
                  {feature.description}
                </p>

                {/* Decorative corner */}
                <div className={`absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 ${color.border} rounded-tr-xl opacity-50`}></div>
                <div className={`absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 ${color.border} rounded-bl-xl opacity-50`}></div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default Features;
